import type { FC, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext } from 'react';
import { getProvider } from '@provider-directory/app/actions/get-provider';
import { type ProviderDetailsResource } from '@provider-directory/types/providers.contracts';

export interface ProviderContextType {
  provider: ProviderDetailsResource | null;
  isLoading: boolean;
}

export const ProviderContext = createContext<ProviderContextType | undefined>(
  undefined,
);

export const ProviderContextProvider: FC<{
  children: ReactNode;
  npi: string | null;
}> = ({ children, npi }) => {
  const { data: provider, isLoading } = useQuery({
    queryKey: ['provider', npi],
    queryFn: () => {
      if (!npi) return Promise.resolve(null);
      return getProvider(npi);
    },
    enabled: !!npi,
  });

  return (
    <ProviderContext.Provider value={{ isLoading, provider: provider ?? null }}>
      {children}
    </ProviderContext.Provider>
  );
};

export const useProvider = (): ProviderContextType => {
  const context = useContext(ProviderContext);
  if (!context) {
    throw new Error('useProvider must be used within a ProviderContext');
  }
  return context;
};
